import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ShipmentManagementService } from '../../../services/shipment-management.service';

// Interface for email response structure
interface EmailResponse {
  email: string;
  routing: string;
}

@Component({
  selector: 'app-mail-detail-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './mail-detail-popup.component.html',
  styleUrls: ['./mail-detail-popup.component.css']
})
export class MailDetailPopupComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() selectedWaybillCodes: string[] = [];
  @Input() waybills: any[] = []; // Array delle bolle complete per accedere a waybillDeliveryType
  @Output() confirmed = new EventEmitter<{ 
    waybillCodes: string[], 
    mailType: string,
    routingData?: { routingCode: string; routingDate: string; waybillCodes: string[] }[],
    routingEmails: string[]
  }>();
  @Output() closed = new EventEmitter<void>();

  // Local copy of waybill codes that can be modified
  localWaybillCodes: string[] = [];
  
  // Loading state for the send button
  isSending = false;

  // Mail type toggle - true for "Invia Singola Bolla", false for "Invia Intero Instradamento"
  isMailPerWaybill = true;

  // Routing data for "Invia Intero Instradamento" mode
  routingData: { routingCode: string; routingDate: string; waybillCodes: string[] }[] = [];

  // Email management
  routingEmails: string[] = [];
  isLoadingEmails = false;
  isRoutingEmailsVisible = false; // Controls visibility of routing emails section

  // Individual item email management
  itemEmailsMap: Map<string, string[]> = new Map(); // key: waybillCode or routingCode, value: emails
  expandedEmailItems: Set<string> = new Set(); // items with expanded email lists
  newEmailInputs: Map<string, string> = new Map(); // new email inputs for each item
  isLoadingItemEmails: Set<string> = new Set(); // loading states for individual items

  constructor(private shipmentService: ShipmentManagementService) { }

  ngOnInit(): void {
    // Initialize local copy
    this.resetForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      if (changes['visible'].currentValue === true) {
        // Popup diventa visibile - forza il reset
        console.log('Mail popup becoming visible, forcing reset');
        // Svuota tutto prima di resettare
        this.localWaybillCodes = [];
        this.routingData = [];
        this.isMailPerWaybill = true;
        this.isSending = false;
        this.routingEmails = [];
        this.itemEmailsMap.clear();
        this.expandedEmailItems.clear();
        this.newEmailInputs.clear();
        this.isLoadingItemEmails.clear();
        // Poi ricarica con i nuovi dati e carica le email
        setTimeout(() => {
          this.resetForm();
          this.loadRoutingEmails();
        }, 0);
      } else if (changes['visible'].currentValue === false && changes['visible'].previousValue === true) {
        // Popup diventa invisibile - forza la pulizia
        console.log('Mail popup becoming invisible, forcing cleanup');
        this.localWaybillCodes = [];
        this.routingData = [];
        this.isMailPerWaybill = true;
        this.isSending = false;
        this.routingEmails = [];
        this.isLoadingEmails = false;
        this.itemEmailsMap.clear();
        this.expandedEmailItems.clear();
        this.newEmailInputs.clear();
        this.isLoadingItemEmails.clear();
      }
    }
  }

  resetForm(): void {
    // Forza sempre il reset completo con le nuove bolle selezionate
    this.localWaybillCodes = [...this.selectedWaybillCodes];
    this.isSending = false;
    this.isMailPerWaybill = true; // Reset to default option
    this.routingData = [];
    // Reset email management
    this.itemEmailsMap.clear();
    this.expandedEmailItems.clear();
    this.newEmailInputs.clear();
    this.isLoadingItemEmails.clear();
    console.log('Mail popup reset with waybills:', this.localWaybillCodes);
  }

  /**
   * Load routing emails from backend
   */
  loadRoutingEmails(): void {
    // Raccoglie tutti i codici di instradamento dalle bolle
    const routingCodes = this.getUniqueRoutingCodes();
    
    if (routingCodes.length === 0) {
      console.log('No routing codes found, skipping email loading');
      return;
    }

    console.log('Loading emails for routing codes:', routingCodes);
    this.isLoadingEmails = true;
    
    this.shipmentService.getEmailsForRoutings(routingCodes).subscribe({
      next: (emailsResponse: EmailResponse[] | any) => {
        console.log('Loaded routing emails:', emailsResponse);
        
        // Handle new array structure: [{email: string, routing: string}]
        let allEmails: string[] = [];
        
        if (Array.isArray(emailsResponse)) {
          // Check if it's the new structure with email and routing properties
          if (emailsResponse.length > 0 && emailsResponse[0].hasOwnProperty('email') && emailsResponse[0].hasOwnProperty('routing')) {
            // New structure: array of EmailResponse objects
            allEmails = emailsResponse
              .filter((item: EmailResponse) => item && item.email && typeof item.email === 'string')
              .map((item: EmailResponse) => item.email);
            
            // Pre-load emails for each item (waybill or routing)
            this.preLoadItemEmails(emailsResponse);
          } else {
            // Legacy: simple array of strings
            allEmails = emailsResponse.filter((email: any) => typeof email === 'string');
          }
          
          // Remove duplicates
          allEmails = [...new Set(allEmails)];
        } else if (typeof emailsResponse === 'object' && emailsResponse !== null) {
          // Legacy support: Response is an object with routing codes as keys
          for (const routingCode of routingCodes) {
            const emailValue = emailsResponse[routingCode];
            if (emailValue) {
              if (Array.isArray(emailValue)) {
                allEmails.push(...emailValue);
              } else if (typeof emailValue === 'string') {
                allEmails.push(emailValue);
              }
            }
          }
          // Remove duplicates
          allEmails = [...new Set(allEmails)];
          
          // Pre-load emails for each item from legacy format
          this.preLoadItemEmailsLegacy(emailsResponse);
        }
        
        this.routingEmails = allEmails;
        this.isLoadingEmails = false;
      },
      error: (error) => {
        console.error('Error loading routing emails:', error);
        this.routingEmails = [];
        this.isLoadingEmails = false;
      }
    });
  }

  /**
   * Pre-load emails for all items from new format response
   */
  private preLoadItemEmails(emailsResponse: EmailResponse[]): void {
    // Pre-load emails for waybills
    this.localWaybillCodes.forEach(waybillCode => {
      const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
      if (waybill && waybill.waybillDeliveryType) {
        const emails = emailsResponse
          .filter((item: EmailResponse) => item && item.email && item.routing === waybill.waybillDeliveryType)
          .map((item: EmailResponse) => item.email);
        this.itemEmailsMap.set(waybillCode, [...emails]);
      }
    });

    // Pre-load emails for routings
    this.routingData.forEach(routing => {
      const emails = emailsResponse
        .filter((item: EmailResponse) => item && item.email && item.routing === routing.routingCode)
        .map((item: EmailResponse) => item.email);
      this.itemEmailsMap.set(routing.routingCode, [...emails]);
    });
  }

  /**
   * Pre-load emails for all items from legacy format response
   */
  private preLoadItemEmailsLegacy(emailsResponse: any): void {
    // Pre-load emails for waybills
    this.localWaybillCodes.forEach(waybillCode => {
      const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
      if (waybill && waybill.waybillDeliveryType) {
        const emailValue = emailsResponse[waybill.waybillDeliveryType];
        let emails: string[] = [];
        if (emailValue) {
          if (Array.isArray(emailValue)) {
            emails = emailValue;
          } else if (typeof emailValue === 'string') {
            emails = [emailValue];
          }
        }
        this.itemEmailsMap.set(waybillCode, [...emails]);
      }
    });

    // Pre-load emails for routings
    this.routingData.forEach(routing => {
      const emailValue = emailsResponse[routing.routingCode];
      let emails: string[] = [];
      if (emailValue) {
        if (Array.isArray(emailValue)) {
          emails = emailValue;
        } else if (typeof emailValue === 'string') {
          emails = [emailValue];
        }
      }
      this.itemEmailsMap.set(routing.routingCode, [...emails]);
    });
  }

  /**
   * Get unique routing codes from waybills
   */
  private getUniqueRoutingCodes(): string[] {
    const routingCodes = new Set<string>();
    
    this.localWaybillCodes.forEach(waybillCode => {
      // Trova la bolla corrispondente nei dati completi
      const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
      
      if (waybill && waybill.waybillDeliveryType) {
        routingCodes.add(waybill.waybillDeliveryType);
      }
    });
    
    return Array.from(routingCodes);
  }

  /**
   * Toggle email list for a specific item (waybill or routing)
   */
  toggleItemEmails(itemId: string): void {
    if (this.expandedEmailItems.has(itemId)) {
      this.expandedEmailItems.delete(itemId);
    } else {
      this.expandedEmailItems.add(itemId);
      this.loadItemEmails(itemId);
    }
  }

  /**
   * Load emails for a specific item
   */
  loadItemEmails(itemId: string): void {
    // If emails already loaded, don't reload
    if (this.itemEmailsMap.has(itemId)) {
      return;
    }

    const routingCode = this.getRoutingCodeForItem(itemId);
    if (!routingCode) {
      console.warn('No routing code found for item:', itemId);
      return;
    }

    this.isLoadingItemEmails.add(itemId);
    
    this.shipmentService.getEmailsForRoutings([routingCode]).subscribe({
      next: (emailsResponse: EmailResponse[] | any) => {
        console.log('Raw email response for item:', emailsResponse);
        
        // Handle new array structure: [{email: string, routing: string}]
        let emails: string[] = [];
        
        if (Array.isArray(emailsResponse)) {
          // Check if it's the new structure with email and routing properties
          if (emailsResponse.length > 0 && emailsResponse[0].hasOwnProperty('email') && emailsResponse[0].hasOwnProperty('routing')) {
            // New structure: filter by routing code and extract emails
            emails = emailsResponse
              .filter((item: EmailResponse) => item && item.email && item.routing === routingCode)
              .map((item: EmailResponse) => item.email);
          } else {
            // Legacy: simple array of strings
            emails = emailsResponse.filter((email: any) => typeof email === 'string');
          }
        } else if (typeof emailsResponse === 'object' && emailsResponse !== null) {
          // Legacy support: Response is an object with routing codes as keys
          const emailValue = emailsResponse[routingCode];
          if (emailValue) {
            if (Array.isArray(emailValue)) {
              emails = emailValue;
            } else if (typeof emailValue === 'string') {
              // Single email string - convert to array
              emails = [emailValue];
            }
          }
        } else if (typeof emailsResponse === 'string') {
          // Response is a single string
          emails = [emailsResponse];
        }
        
        this.itemEmailsMap.set(itemId, [...emails]);
        this.isLoadingItemEmails.delete(itemId);
        console.log(`Loaded emails for item ${itemId}:`, emails);
      },
      error: (error) => {
        console.error(`Error loading emails for item ${itemId}:`, error);
        this.itemEmailsMap.set(itemId, []);
        this.isLoadingItemEmails.delete(itemId);
      }
    });
  }

  /**
   * Get routing code for a specific item
   */
  private getRoutingCodeForItem(itemId: string): string | null {
    // Check if it's a waybill code
    const waybill = this.waybills.find(w => w.waybillCode === itemId);
    if (waybill) {
      return waybill.waybillDeliveryType || null;
    }
    
    // Check if it's a routing code
    const routing = this.routingData.find(r => r.routingCode === itemId);
    if (routing) {
      return routing.routingCode;
    }
    
    return null;
  }

  /**
   * Add email for a specific item
   */
  addEmailToItem(itemId: string): void {
    const newEmail = this.newEmailInputs.get(itemId)?.trim();
    if (!newEmail || !this.isValidEmail(newEmail)) {
      return;
    }

    const currentEmails = this.itemEmailsMap.get(itemId) || [];
    if (!currentEmails.includes(newEmail)) {
      currentEmails.push(newEmail);
      this.itemEmailsMap.set(itemId, currentEmails);
      this.newEmailInputs.set(itemId, '');
    }
  }

  /**
   * Remove email from a specific item
   */
  removeEmailFromItem(itemId: string, email: string): void {
    const currentEmails = this.itemEmailsMap.get(itemId) || [];
    const updatedEmails = currentEmails.filter(e => e !== email);
    this.itemEmailsMap.set(itemId, updatedEmails);
  }

  /**
   * Handle Enter key press in item email input
   */
  onItemEmailKeyPress(event: KeyboardEvent, itemId: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addEmailToItem(itemId);
    }
  }

  /**
   * Check if item has expanded emails
   */
  isItemEmailExpanded(itemId: string): boolean {
    return this.expandedEmailItems.has(itemId);
  }

  /**
   * Get emails for a specific item
   */
  getItemEmails(itemId: string): string[] {
    return this.itemEmailsMap.get(itemId) || [];
  }

  /**
   * Check if item emails are loading
   */
  isItemEmailsLoading(itemId: string): boolean {
    return this.isLoadingItemEmails.has(itemId);
  }

  /**
   * Get new email input value for item
   */
  getNewEmailInput(itemId: string): string {
    return this.newEmailInputs.get(itemId) || '';
  }

  /**
   * Set new email input value for item
   */
  setNewEmailInput(itemId: string, value: string): void {
    this.newEmailInputs.set(itemId, value);
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Toggle visibility of routing emails section
   */
  toggleRoutingEmailsVisibility(): void {
    this.isRoutingEmailsVisible = !this.isRoutingEmailsVisible;
  }

  setMailType(isPerWaybill: boolean): void {
    this.isMailPerWaybill = isPerWaybill;
    
    if (!isPerWaybill) {
      // Quando si cambia a "Invia Intero Instradamento", genera i dati di instradamento
      this.generateRoutingData();
      // Pre-carica le email per i routing appena generati
      this.preLoadRoutingEmails();
    }
  }

  private generateRoutingData(): void {
    // Raggruppa le bolle per waybillDeliveryType (instradamento)
    const routingMap = new Map<string, { routingDate: string; waybillCodes: string[] }>();
    
    this.localWaybillCodes.forEach(waybillCode => {
      // Trova la bolla corrispondente nei dati completi
      const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
      
      if (waybill) {
        const deliveryType = waybill.waybillDeliveryType || 'Da Assegnare';
        
        if (!routingMap.has(deliveryType)) {
          // Per la data, usa la data della bolla o una data di esempio
          const routingDate = waybill.waybillDate || new Date().toISOString().split('T')[0];
          
          routingMap.set(deliveryType, {
            routingDate: routingDate,
            waybillCodes: []
          });
        }
        
        routingMap.get(deliveryType)!.waybillCodes.push(waybillCode);
      }
    });
    
    // Converte la mappa in array
    this.routingData = Array.from(routingMap.entries()).map(([routingCode, data]) => ({
      routingCode,
      routingDate: data.routingDate,
      waybillCodes: data.waybillCodes
    }));
  }

  /**
   * Pre-load emails for routing when switching to routing mode
   */
  private preLoadRoutingEmails(): void {
    const routingCodesToLoad = this.routingData
      .map(r => r.routingCode)
      .filter(code => !this.itemEmailsMap.has(code));
    
    if (routingCodesToLoad.length === 0) {
      return;
    }

    this.shipmentService.getEmailsForRoutings(routingCodesToLoad).subscribe({
      next: (emailsResponse: EmailResponse[] | any) => {
        if (Array.isArray(emailsResponse)) {
          if (emailsResponse.length > 0 && emailsResponse[0].hasOwnProperty('email') && emailsResponse[0].hasOwnProperty('routing')) {
            // New structure: array of EmailResponse objects
            this.routingData.forEach(routing => {
              if (!this.itemEmailsMap.has(routing.routingCode)) {
                const emails = emailsResponse
                  .filter((item: EmailResponse) => item && item.email && item.routing === routing.routingCode)
                  .map((item: EmailResponse) => item.email);
                this.itemEmailsMap.set(routing.routingCode, [...emails]);
              }
            });
          }
        } else if (typeof emailsResponse === 'object' && emailsResponse !== null) {
          // Legacy format
          this.routingData.forEach(routing => {
            if (!this.itemEmailsMap.has(routing.routingCode)) {
              const emailValue = emailsResponse[routing.routingCode];
              let emails: string[] = [];
              if (emailValue) {
                if (Array.isArray(emailValue)) {
                  emails = emailValue;
                } else if (typeof emailValue === 'string') {
                  emails = [emailValue];
                }
              }
              this.itemEmailsMap.set(routing.routingCode, [...emails]);
            }
          });
        }
      },
      error: (error) => {
        console.error('Error pre-loading routing emails:', error);
      }
    });
  }

  removeWaybill(waybillCode: string): void {
    this.localWaybillCodes = this.localWaybillCodes.filter(code => code !== waybillCode);
    
    // Se siamo in modalità routing, rigenera i dati di instradamento
    if (!this.isMailPerWaybill) {
      this.generateRoutingData();
    }
  }

  removeRouting(routingCode: string): void {
    // Trova l'instradamento da rimuovere
    const routingToRemove = this.routingData.find(r => r.routingCode === routingCode);
    
    if (routingToRemove) {
      // Rimuovi tutte le bolle associate a questo instradamento
      routingToRemove.waybillCodes.forEach(waybillCode => {
        this.localWaybillCodes = this.localWaybillCodes.filter(code => code !== waybillCode);
      });
      
      // Rimuovi l'instradamento dalla lista
      this.routingData = this.routingData.filter(r => r.routingCode !== routingCode);
    }
  }

  onConfirm(): void {
    if (this.isMailPerWaybill) {
      // Modalità "Invia Singola Bolla"
      if (this.localWaybillCodes.length === 0) {
        return;
      }
    } else {
      // Modalità "Invia Intero Instradamento" - invia tutti gli instradamenti
      if (this.routingData.length === 0) {
        return;
      }
    }

    this.isSending = true;

    // Genera il JSON dettagliato con email per ogni elemento
    const mailData = this.generateMailData();
    
    // Stampa il JSON in console
    console.log('=== MAIL DATA JSON ===');
    console.log(JSON.stringify(mailData, null, 2));
    console.log('=====================');

    const data = {
      waybillCodes: this.isMailPerWaybill ? this.localWaybillCodes : this.getAllWaybillCodesFromRoutings(),
      mailType: this.isMailPerWaybill ? 'per_waybill' : 'per_routing',
      routingData: this.isMailPerWaybill ? undefined : this.routingData,
      routingEmails: this.routingEmails,
      detailedMailData: mailData // Aggiungo i dati dettagliati
    };
    
    this.confirmed.emit(data);
    this.close();
  }

  /**
   * Genera un JSON dettagliato con tutte le bolle e le email associate
   */
  private generateMailData(): any {
    const result = {
      mailType: this.isMailPerWaybill ? 'per_waybill' : 'per_routing',
      timestamp: new Date().toISOString(),
      data: [] as any[]
    };

    if (this.isMailPerWaybill) {
      // Modalità per singola bolla
      this.localWaybillCodes.forEach(waybillCode => {
        const emails = this.getItemEmails(waybillCode);
        result.data.push({
          type: 'waybill',
          code: waybillCode,
          emails: emails.length > 0 ? emails : ['ALL_MAILS_BY_TOUR']
        });
      });
    } else {
      // Modalità per instradamento
      this.routingData.forEach(routing => {
        const emails = this.getItemEmails(routing.routingCode);
        result.data.push({
          type: 'routing',
          routingCode: routing.routingCode,
          routingDate: routing.routingDate,
          waybillCodes: routing.waybillCodes,
          emails: emails.length > 0 ? emails : ['ALL_MAILS_BY_TOUR']
        });
      });
    }

    return result;
  }

  private getAllWaybillCodesFromRoutings(): string[] {
    const waybillCodes: string[] = [];
    this.routingData.forEach(routing => {
      waybillCodes.push(...routing.waybillCodes);
    });
    return waybillCodes;
  }

  close(): void {
    this.visible = false;
    // Svuota completamente i dati alla chiusura
    this.localWaybillCodes = [];
    this.routingData = [];
    this.isSending = false;
    this.isMailPerWaybill = true;
    this.routingEmails = [];
    this.isLoadingEmails = false;
    // Reset individual email management
    this.itemEmailsMap.clear();
    this.expandedEmailItems.clear();
    this.newEmailInputs.clear();
    this.isLoadingItemEmails.clear();
    console.log('Mail popup closed and cleared');
    this.closed.emit();
  }

  onBackdropClick(event: Event): void {
    // Close popup only if clicking on backdrop, not on content
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  /**
   * TrackBy function for ngFor optimization
   */
  trackByWaybillCode(index: number, waybillCode: string): string {
    return waybillCode;
  }

  /**
   * TrackBy function for routing ngFor optimization
   */
  trackByRoutingCode(index: number, routing: { routingCode: string; routingDate: string; waybillCodes: string[] }): string {
    return routing.routingCode;
  }

  /**
   * TrackBy function for email ngFor optimization
   */
  trackByEmail(index: number, email: string): string {
    return email;
  }
}
